import { Prompt, GPT4oConfig, PromptConfig } from '../types/prompt';
import { getAuthHeaders, getBaseUrl } from './apiClient';

export const promptService = {
  savePrompt: async (config: PromptConfig): Promise<{ id: string, version: string }> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/secure/prompt-configurations`;
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify(config)
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Failed to save prompt: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error in savePrompt:', error);
        throw error;
    }
  },

  async getPrompt(category: string, id: string): Promise<PromptConfig> {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/secure/prompt-configurations/${category}/${id}`;
    
    const response = await fetch(url, { headers: await getAuthHeaders() });

    if (!response.ok) {
      const text = await response.text();
      console.error('Error fetching prompt:', {
        status: response.status,
        statusText: response.statusText,
        body: text
      });
      throw new Error(`Failed to fetch prompt: ${response.statusText}`);
    }

    try {
      return await response.json();
    } catch (error) {
      console.error('Error parsing response:', error);
      throw new Error('Failed to parse prompt data');
    }
  },

  executePrompt: async (
    prompts: Prompt[],
    configuration: GPT4oConfig,
    projectType: string
  ): Promise<{ 
    id: string;
    s3_key: string;
    content: string | {
      questions: Array<{
        content: {
          question_text: string;
          options: Array<{
            option_id: string;
            answer_text: string;
            explanation: string;
            is_correct: boolean;
          }>;
        };
        metadata: {
          question_id: string;
          created_at: string;
          ability: string;
          level: string;
          discrimination_factor: number;
          skills: Array<{ skill: string }>;
        };
      }>;
    };
    message: string;
  }> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/secure/generate-content`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          prompts,
          configuration,
          type: projectType
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error executing prompt:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to execute prompt: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in executePrompt:', error);
      throw error;
    }
  },

  listPrompts: async (category: string) => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/secure/prompt-configurations?category=${category}`;
    console.log('Fetching prompts from URL:', url);
    
    try {
        const response = await fetch(url, { headers: await getAuthHeaders() });
        console.log('Response status:', response.status);
        
        if (!response.ok) {
            const text = await response.text();
            console.error('Raw error response:', text);
            throw new Error(`Failed to load prompts: ${response.status} ${response.statusText}`);
        }
        
        const prompts = await response.json();
        console.log('Loaded prompts:', prompts);
        return prompts;
    } catch (error) {
        console.error('Error in listPrompts:', error);
        throw error;
    }
  }
}; 